// Renglones del catálogo de extras sobre la `DataTable` compartida: producto, precio, chip de pago,
// existencia y acción. Un modelo de accesorio con variantes va en un solo renglón con «Elegir».
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { CellLines, DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { formatMXN } from '@/shared/lib/format/money';
import { catalogDisplayName, itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import {
    variantCountLabel,
    type CatalogEntry,
    type ModelGroup,
} from '@/domains/sales/portal/catalog/lib/modelGroups';
import { itemLabelOf, maxQtyFor } from '../cartLines';
import type { CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';
import { ExtraQuantityStepper, type ExtraPieceAction } from './ExtraQuantityStepper';
import { FinancingBadge } from './FinancingBadge';
import { StockChip } from './StockChip';

const NO_DESCRIPTIONS = new Map<string, string>();

export type ExtraEntry = CatalogEntry;

const money = (cents: number): string => formatMXN(cents / 100);

/** Precio por pieza: el rebajado si la promo lo baja; si no, el de lista. */
export function netPriceOf(item: CatalogItem, benefits: CardSaleBenefits | undefined): number {
    const discounted = benefits?.discountedPriceCents ?? null;
    return discounted !== null && discounted < item.cashPriceCents
        ? discounted
        : item.cashPriceCents;
}

/** Precio de la celda: con rebaja, el de lista tachado arriba y el rebajado abajo. */
export function ExtraPrice({
    listCents,
    netCents,
    prefix,
}: {
    listCents: number;
    netCents: number;
    /** «desde» para un modelo con precios distintos. */
    prefix?: string;
}) {
    const label = `${prefix ? `${prefix} ` : ''}${money(netCents)}`;
    if (netCents >= listCents) {
        return (
            <span data-testid="extra-price" className="block truncate font-semibold">
                {label}
            </span>
        );
    }
    return (
        <>
            <s
                data-testid="extra-original-price"
                className="block truncate text-[10px] leading-tight text-muted-foreground"
            >
                {money(listCents)}
            </s>
            <span data-testid="extra-price" className="block truncate font-semibold text-success">
                {label}
            </span>
        </>
    );
}

/** El chip del grupo solo si todas sus variantes dicen lo mismo. */
function groupFinanceable(group: ModelGroup): boolean | undefined {
    const values = new Set(group.variants.map((v) => v.financeable));
    if (values.size !== 1) return undefined;
    return group.variants[0].financeable;
}

export function groupDisplayName(group: ModelGroup): string {
    return itemDisplayName({ brand: group.brand, model: group.model });
}

/** «Marca · etiqueta · SKU», sin huecos. */
function secondaryOf(item: CatalogItem): string {
    return [item.brand.trim(), itemLabelOf(item), item.sku]
        .filter((part) => part !== '')
        .join(' · ');
}

export interface ExtrasTableProps {
    entries: readonly ExtraEntry[];
    /** Nombre de la tabla para el lector de pantalla. */
    caption: string;
    counts: ReadonlyMap<string, number>;
    pendingOf: (sku: string) => ExtraPieceAction | null;
    benefits: (item: CatalogItem) => CardSaleBenefits | undefined;
    /** `false` sin SureKeep confirmado: no se pinta el chip Crédito/Contado. */
    showFinancing: boolean;
    onAdd: (sku: string) => void;
    onRemove: (sku: string) => void;
    onChooseGroup: (group: ModelGroup) => void;
    /** Pinta renglones de esqueleto mientras llega el catálogo. */
    loading?: boolean;
}

export function ExtrasTable({
    entries,
    caption,
    counts,
    pendingOf,
    benefits,
    showFinancing,
    onAdd,
    onRemove,
    onChooseGroup,
    loading = false,
}: ExtrasTableProps) {
    const groupCount = (group: ModelGroup): number =>
        group.variants.reduce((sum, v) => sum + (counts.get(v.sku) ?? 0), 0);

    const columns: DataTableColumn<ExtraEntry>[] = [
        {
            key: 'product',
            label: 'Producto',
            cell: (entry) => {
                if (entry.kind === 'model') {
                    const name = groupDisplayName(entry.group);
                    return (
                        <CellLines
                            primary={name}
                            primaryClassName="font-semibold"
                            secondary={`${entry.group.brand} · ${variantCountLabel(entry.group)}`}
                        />
                    );
                }
                const name = catalogDisplayName(entry.item, NO_DESCRIPTIONS);
                return (
                    <CellLines
                        primary={name}
                        primaryClassName="font-semibold"
                        secondary={secondaryOf(entry.item)}
                    />
                );
            },
        },
        {
            key: 'price',
            label: 'Precio',
            width: '104px',
            numeric: true,
            cell: (entry) => {
                if (entry.kind === 'item') {
                    const net = netPriceOf(entry.item, benefits(entry.item));
                    return <ExtraPrice listCents={entry.item.cashPriceCents} netCents={net} />;
                }
                const cheapest = entry.group.variants.reduce((best, v) =>
                    netPriceOf(v, benefits(v)) < netPriceOf(best, benefits(best)) ? v : best,
                );
                const varies = entry.group.minPriceCents !== entry.group.maxPriceCents;
                return (
                    <ExtraPrice
                        listCents={cheapest.cashPriceCents}
                        netCents={netPriceOf(cheapest, benefits(cheapest))}
                        prefix={varies ? 'desde' : undefined}
                    />
                );
            },
        },
        {
            key: 'financing',
            label: 'Pago',
            width: '84px',
            cellOverflow: 'nowrap',
            cell: (entry) => {
                const financeable =
                    entry.kind === 'item' ? entry.item.financeable : groupFinanceable(entry.group);
                if (!showFinancing || financeable === undefined) return null;
                return <FinancingBadge financeable={financeable} />;
            },
        },
        {
            key: 'stock',
            label: 'Existencia',
            width: '112px',
            cellOverflow: 'nowrap',
            cell: (entry) =>
                entry.kind === 'item' ? (
                    <StockChip item={entry.item} />
                ) : (
                    <span className="text-muted-foreground tabular-nums">
                        {String(entry.group.stockTotal)} en total
                    </span>
                ),
        },
        {
            key: 'action',
            label: <span className="sr-only">Acción</span>,
            width: '136px',
            cellOverflow: 'nowrap',
            cellClassName: () => 'text-right',
            cell: (entry) => {
                if (entry.kind === 'model') {
                    const inSale = groupCount(entry.group);
                    const name = groupDisplayName(entry.group);
                    const busy = entry.group.variants.some((v) => pendingOf(v.sku) !== null);
                    return (
                        <Button
                            size="sm"
                            variant="outline"
                            className="min-w-24"
                            disabled={busy || (entry.group.soldOut && inSale === 0)}
                            isLoading={busy}
                            aria-label={`Elegir variante de ${name}`}
                            onClick={() => {
                                onChooseGroup(entry.group);
                            }}
                        >
                            {inSale > 0 ? `Agregar más (${String(inSale)})` : 'Elegir'}
                        </Button>
                    );
                }
                const { item } = entry;
                const count = counts.get(item.sku) ?? 0;
                return (
                    <ExtraQuantityStepper
                        sku={item.sku}
                        name={catalogDisplayName(item, NO_DESCRIPTIONS)}
                        count={count}
                        pending={pendingOf(item.sku)}
                        canAdd={count < maxQtyFor(item)}
                        onAdd={() => {
                            onAdd(item.sku);
                        }}
                        onRemove={() => {
                            onRemove(item.sku);
                        }}
                    />
                );
            },
        },
    ];

    return (
        <DataTable
            caption={caption}
            columns={columns}
            rows={entries}
            rowKey={(entry) => entry.key}
            density="compact"
            isLoading={loading}
            skeletonRows={4}
            rowClassName={(entry) =>
                cn(
                    'h-14',
                    entry.kind === 'item' &&
                        (counts.get(entry.item.sku) ?? 0) > 0 &&
                        'bg-accent/30',
                )
            }
            className="overflow-visible"
        />
    );
}
